import { PhoneCall, MessageSquare, Settings, FileText, Save, Pill, Download } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { capturePhotoDataUrl } from "../lib/nativeCamera";

const SOCKET_SERVER_URL = "https://backend-qureo-production-fab5.up.railway.app";
const API_BASE_URL = "https://backend-qureo-production-fab5.up.railway.app/api";// Your backend API URL

const hasTurnServer = (iceServers = []) => {
  return iceServers.some((server) => {
    const urls = Array.isArray(server?.urls) ? server.urls : [server?.urls];
    return urls.some((url) => typeof url === "string" && /^turns?:/i.test(url));
  });
};

const describeIceServers = (iceServers = []) => {
  return iceServers.map((server, index) => {
    const urls = Array.isArray(server?.urls) ? server.urls : [server?.urls];
    return {
      index,
      urls: urls.filter((url) => typeof url === "string"),
      hasUsername: Boolean(server?.username),
      hasCredential: Boolean(server?.credential),
    };
  });
};

const resolvePaidDurationMinutes = (appointment) => {
  const candidates = [
    appointment?.durationMinutes,
    appointment?.duration,
    appointment?.paidDurationMinutes,
    appointment?.paidDuration,
    appointment?.bookingDuration,
  ];

  for (const value of candidates) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.round(parsed);
    }
  }

  return 30;
};

const PatientCallRoom = () => {
  const {id} = useParams()
  console.log(id)
  const [inCall, setInCall] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showPrescription, setShowPrescription] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [micEnabled, setMicEnabled] = useState(true);
  const [camEnabled, setCamEnabled] = useState(true);
  const [callTime, setCallTime] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState("Connecting...");
  const [doctorPrescription, setDoctorPrescription] = useState(null);
  const [saving, setSaving] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [loadingChat, setLoadingChat] = useState(false);
  const [showEndCallModal, setShowEndCallModal] = useState(false);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const socketRef = useRef(null);
  const iceQueue = useRef([]);
  const peerIdRef = useRef(null);
  const messagesEndRef = useRef(null);
  const localStreamRef = useRef(null);
  const isInitializingPeerRef = useRef(false);
  const peerInitPromiseRef = useRef(null);
  const hasTurnRef = useRef(false);
  const lastIceServersRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const autoExpireHandledRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const appointment = location.state || null;
  const consultationId = appointment?.consultationId || appointment?.id || id;
  const roomId = appointment?.roomId || appointment?.consultationRoomId || appointment?.room || id || consultationId;
  const patientId = localStorage.getItem('userId') || appointment?.patientId || 'patient-123';
  const patientName = localStorage.getItem('userName') || appointment?.patientName || 'Patient';
  const paidDurationMinutes = resolvePaidDurationMinutes(appointment);
  const paidDurationSeconds = paidDurationMinutes * 60;
  const [remainingTime, setRemainingTime] = useState(paidDurationSeconds);

  // Configure axios defaults (no token auth needed)
  axios.defaults.baseURL = API_BASE_URL;

  console.log("PatientCallRoom appointment:", appointment);
  console.log("Consultation ID:", consultationId);
  console.log("Resolved room ID:", roomId);

  const getPhotoWithCapacitor = async () => {
    const result = await capturePhotoDataUrl();
    if (!result.ok) {
      console.error("Camera error:", result.error);
      return null;
    }

    return result.image;
  };

  // ========== API FUNCTIONS ==========

  // Load chat history from backend
  const loadChatHistory = async () => {
    if (!consultationId) {
      console.log("No consultation ID, skipping chat load");
      return;
    }

    setLoadingChat(true);
    try {
      console.log("Loading chat for consultation:", consultationId);
      
      const response = await axios.get(`/consultations/${consultationId}/chat`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        const loadedMessages = response.data.data?.chat || response.data.chat || [];
        console.log("Loaded chat messages:", loadedMessages.length);
        
        setMessages(loadedMessages);
        
        // Check for prescription in loaded messages
        const prescriptionMsg = loadedMessages.find(msg => 
          msg.type === 'prescription' && msg.data
        );
        if (prescriptionMsg?.data) {
          console.log("Found existing prescription");
          setDoctorPrescription(prescriptionMsg.data);
        }
      }
    } catch (error) {
      console.error("Error loading chat history:", error.response?.data || error.message);
      
      // If endpoint doesn't exist, use mock data for development
      if (error.response?.status === 404) {
        console.log("Chat endpoint not found, using mock data");
        const mockMessages = [
          
        ];
        setMessages(mockMessages);
      }
    } finally {
      setLoadingChat(false);
    }
  };

  // Save chat message to backend
  const saveChatMessage = async (message, type = 'text') => {
    if (!consultationId) {
      console.log("Cannot save chat - missing consultation ID");
      return;
    }

    try {
      const messageData = {
        message: message.text,
        senderType: message.sender,
        senderId: message.senderId,
        senderName: message.senderName,
        messageType: type
      };

      await axios.post(`/consultations/${consultationId}/chat/save`, messageData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log("✅ Chat message saved to backend");
    } catch (error) {
      console.error("Error saving chat message:", error.response?.data || error.message);
      // Continue even if save fails - message is already in local state
    }
  };


  //drain
  // Add this function to your PatientCallRoom component, right after the initPeerConnection function:

// DRAIN ICE CANDIDATE QUEUE
const drainIceQueue = async () => {
  if (!pcRef.current || iceQueue.current.length === 0) return;
  
  console.log("Patient draining ICE queue:", iceQueue.current.length);
  
  for (const candidate of iceQueue.current) {
    try {
      await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (err) {
      console.error("Patient error adding queued ICE candidate:", err);
    }
  }
  
  iceQueue.current = [];
};

  // Save prescription to backend
  const savePrescription = async (prescriptionData) => {
    if (!consultationId) {
      console.log("Cannot save prescription - missing consultation ID");
      return;
    }

    try {
      const prescriptionToSave = {
        medicines: prescriptionData.medicines || [],
        instructions: prescriptionData.instructions || '',
        followUpDate: prescriptionData.followUpDate || null,
        labTests: prescriptionData.labTests || [],
        doctorNotes: prescriptionData.doctorNotes || '',
        diagnosis: prescriptionData.diagnosis || '',
        createdBy: appointment?.doctorId || 'doc-123'
      };

      const response = await axios.post(
        `/consultations/${consultationId}/prescription/create`,
        prescriptionToSave,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      console.log("✅ Prescription saved to backend:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error saving prescription:", error.response?.data || error.message);
      throw error;
    }
  };

  // Save consultation summary
  const exportChatTranscript = () => {
    if (!Array.isArray(messages) || messages.length === 0) {
      alert("No chat messages to save yet.");
      return false;
    }

    const transcript = messages
      .map((msg) => {
        const timestamp = msg?.timestamp ? new Date(msg.timestamp).toLocaleString() : "Unknown time";
        const sender = msg?.senderName || msg?.sender || "Unknown";
        const text = msg?.text || "";
        return `[${timestamp}] ${sender}: ${text}`;
      })
      .join("\n");

    const blob = new Blob([transcript], { type: "text/plain;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `chat-transcript-${consultationId || "consultation"}-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return true;
  };

  const saveConsultationSummary = async () => {
    if (!consultationId) {
      alert("Cannot save consultation - missing consultation ID");
      return;
    }

    exportChatTranscript();

    setSaving(true);
    try {
      const summary = {
        callDuration: callTime,
        endedAt: new Date().toISOString(),
        consultationNotes: `Video consultation completed. Duration: ${callTime} seconds.`,
        keyFindings: [],
        recommendations: ["Follow prescription instructions", "Schedule follow-up if needed"],
        nextSteps: "Take prescribed medicines and monitor symptoms"
      };

      const response = await axios.post(
        `/consultations/${consultationId}/summary`,
        summary,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        alert("✅ Consultation saved successfully!");
        console.log("Consultation saved:", response.data);
        
        // Redirect to consultations page after 2 seconds
        setTimeout(() => {
          window.location.href = '/patient/consultations';
        }, 2000);
      }
    } catch (error) {
      console.error("Error saving consultation:", error.response?.data || error.message);
      alert("Failed to save consultation. Error: " + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const completeConsultationWhenExpired = async (elapsedCallTime) => {
    if (!consultationId) return;

    try {
      await axios.put(`/consultations/${consultationId}/complete`, {
        reason: "time_elapsed",
        endedAt: new Date().toISOString(),
        callDuration: elapsedCallTime,
      });
    } catch (error) {
      console.error("Error completing consultation after expiry:", error.response?.data || error.message);
    }
  };

  // Download prescription as PDF
  const downloadPrescription = async () => {
    if (!doctorPrescription) return;

    try {
      const response = await axios.post(
        `/prescriptions/download`,
        {
          consultationId,
          prescription: doctorPrescription,
          patientName,
          doctorName: appointment?.doctor_?.name || "Dr. Smith"
        },
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prescription-${consultationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      console.log("✅ Prescription downloaded");
    } catch (error) {
      console.error("Error downloading prescription:", error);
      alert("Could not download prescription. You can take a screenshot instead.");
    }
  };

  // ========== SOCKET & WEBRTC SETUP ==========

  useEffect(() => {
    console.log("🚀 Initializing PatientCallRoom...");
    
    // Load chat history on mount
    loadChatHistory();
    
    const socket = io(SOCKET_SERVER_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });
    
    socketRef.current = socket;

    // Connection events
    socket.on("connect", () => {
      console.log("✅ Patient connected to signaling server");
      setConnectionStatus("Connected, checking relay...");
      socket.emit("webrtc-join-room", roomId);
    });

    socket.on("disconnect", () => {
      console.log("❌ Patient disconnected from signaling server");
      setConnectionStatus("Disconnected");
      setInCall(false);
      setShowEndCallModal(true);
    });

    socket.on("connect_error", (error) => {
      console.error("❌ Socket connection error:", error);
      setConnectionStatus("Connection failed");
    });

    // ICE servers (optional - you might not need this)
    socket.on("ice-servers", async (iceServers) => {
      console.log("Patient received ICE servers:", describeIceServers(Array.isArray(iceServers) ? iceServers : []));
      lastIceServersRef.current = Array.isArray(iceServers) ? iceServers : [];
      hasTurnRef.current = hasTurnServer(lastIceServersRef.current);
      console.log("Patient TURN available:", hasTurnRef.current, "ICE count:", lastIceServersRef.current.length);

      if (!hasTurnRef.current) {
        console.warn("Patient TURN relay unavailable. Last ICE servers:", describeIceServers(lastIceServersRef.current));
        setConnectionStatus("TURN relay unavailable");
        alert("Secure relay (TURN) is unavailable. Calls across different networks may fail. Please try again later.");
        return;
      }

      setConnectionStatus("Relay ready");
      await initPeerConnection(lastIceServersRef.current);
    });

    // User joined event
    socket.on("user-joined", async ({ peerId }) => {
      console.log("👤 Doctor joined:", peerId);
      peerIdRef.current = peerId;
    });

    // WebRTC offer (from doctor)
    socket.on("webrtc-offer", async ({ offer, from }) => {
      console.log("📥 Patient received offer from doctor:", from);
      try {
        if (!hasTurnRef.current) {
          setConnectionStatus("TURN relay required");
          console.warn("Patient blocked offer handling because TURN is unavailable.");
          return;
        }

        if (!pcRef.current) {
          await initPeerConnection(lastIceServersRef.current);
        }
        
        peerIdRef.current = from;

        if (pcRef.current.signalingState !== "stable") {
          console.warn("Patient cannot accept offer - signaling state:", pcRef.current.signalingState);
          return;
        }

        await pcRef.current.setRemoteDescription(new RTCSessionDescription(offer));
        console.log("✅ Patient set remote description");

        const answer = await pcRef.current.createAnswer();
        await pcRef.current.setLocalDescription(answer);
        console.log("✅ Patient created and set local answer");

        socketRef.current.emit("webrtc-answer", { 
          answer, 
          to: from 
        });
        
        setInCall(true);
        console.log("✅ Patient call established");

        await drainIceQueue();
      } catch (err) {
        console.error("❌ Patient error handling offer:", err);
      }
    });

    // ICE candidates from doctor
    socket.on("webrtc-ice-candidate", async ({ candidate, from }) => {
      console.log("🧊 Patient received ICE candidate from doctor:", from);
      if (!candidate) return;

      if (!pcRef.current) {
        iceQueue.current.push(candidate);
        console.log("Patient queued ICE candidate");
        return;
      }

      try {
        await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        console.log("✅ Patient added ICE candidate");
      } catch (err) {
        console.error("❌ Patient failed to add ICE candidate:", err);
      }
    });

    // Chat messages from doctor
    socket.on("chat-message", async ({ message, from }) => {
      console.log("💬 Chat message from doctor:", message);
      
      const newMessageObj = {
        _id: `msg-${Date.now()}`,
        text: message,
        sender: "doctor",
        senderId: from,
        senderName: "Doctor",
        timestamp: new Date().toISOString(),
        type: "text"
      };
      
      // Add to local state
      setMessages(prev => [...prev, newMessageObj]);
      
      // Increment unread count if chat is closed
      if (!chatOpen) {
        setUnreadMessages(prev => prev + 1);
      }
      
      // Save to backend
      await saveChatMessage(newMessageObj);
    });

    // Prescription from doctor
    socket.on("prescription-from-doctor", async (prescriptionData) => {
      console.log("💊 Received prescription from doctor:", prescriptionData);
      
      // Set prescription state
      setDoctorPrescription(prescriptionData);
      
      // Create prescription message
      const prescriptionMessage = {
        _id: `pres-${Date.now()}`,
        text: `Dr. ${appointment?.doctor_?.name || "Doctor"} has prescribed ${prescriptionData.medicines?.length || 0} medicine(s)`,
        sender: "system",
        senderId: "system",
        senderName: "System",
        timestamp: new Date().toISOString(),
        type: "prescription",
        data: prescriptionData
      };
      
      // Add to chat
      setMessages(prev => [...prev, prescriptionMessage]);
      
      // Show notification
      setUnreadMessages(prev => prev + 1);
      
      // Save prescription message to chat history
      await saveChatMessage(prescriptionMessage, 'prescription');
    });

    // User left
    socket.on("user-left", ({ peerId }) => {
      console.log("👋 Doctor left:", peerId);
      setInCall(false);
      setShowEndCallModal(true);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = null;
      }
    });

    return () => {
      console.log("🔴 Cleaning up Patient socket");
      cleanup();
    };
  }, []);

  useEffect(() => {
    const nextDurationSeconds = resolvePaidDurationMinutes(appointment) * 60;
    setRemainingTime(nextDurationSeconds);
    setCallTime(0);
    autoExpireHandledRef.current = false;
  }, [appointment?.consultationId, appointment?.id, appointment?.durationMinutes, appointment?.duration, appointment?.paidDurationMinutes, appointment?.paidDuration]);

  useEffect(() => {
    if (!inCall || showEndCallModal || remainingTime <= 0) {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }

    timerIntervalRef.current = setInterval(() => {
      setCallTime((prev) => prev + 1);
      setRemainingTime((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [inCall, showEndCallModal, remainingTime]);

  useEffect(() => {
    if (!inCall || remainingTime > 0 || autoExpireHandledRef.current) {
      return;
    }

    autoExpireHandledRef.current = true;

    const expiryMessage = {
      _id: `expiry-${Date.now()}`,
      text: "Your time booked has expired.",
      sender: "system",
      senderId: "system",
      senderName: "System",
      timestamp: new Date().toISOString(),
      type: "system"
    };

    setMessages((prev) => [...prev, expiryMessage]);
    if (!chatOpen) {
      setUnreadMessages((prev) => prev + 1);
    }

    saveChatMessage(expiryMessage, "system");
    completeConsultationWhenExpired(callTime);

    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }

    setInCall(false);
    setConnectionStatus("Consultation completed");
    setShowEndCallModal(true);
  }, [inCall, remainingTime, chatOpen, callTime]);

  // ========== CHAT FUNCTIONS ==========

  const sendMessage = () => {
    if (!newMessage.trim() || !socketRef.current) return;
    
    const messageObj = {
      _id: `msg-${Date.now()}`,
      text: newMessage,
      sender: "patient",
      senderId: patientId,
      senderName: "You",
      timestamp: new Date().toISOString(),
      type: "text"
    };
    
    // Send via socket to doctor
    socketRef.current.emit("chat-message", { 
      room: roomId, 
      message: newMessage,
      senderType: "patient",
      senderId: patientId,
      senderName: patientName
    });
    
    // Add to local messages
    setMessages(prev => [...prev, messageObj]);
    setNewMessage("");
    
    // Save to backend
    saveChatMessage(messageObj);
  };

  // ========== WEBRTC FUNCTIONS ==========

  // Use Capacitor Camera for photo capture if available
  const handleTakePhoto = async () => {
    const image = await getPhotoWithCapacitor();
    if (image) {
      // Do something with image.dataUrl, image.base64String, or image.path
      // For example, send to backend or display in UI
      console.log('Captured image:', image);
    }
  };

  const acquireLocalMediaStream = async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      throw new Error("This browser does not support camera/microphone access.");
    }

    const attempts = [
      { video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } }, audio: true },
      { video: true, audio: true },
      { video: { facingMode: "user" }, audio: false },
      { video: true, audio: false },
    ];

    let lastError = null;
    for (const constraints of attempts) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        return stream;
      } catch (err) {
        lastError = err;
        console.warn("Patient getUserMedia attempt failed:", constraints, err?.message || err);
      }
    }

    throw lastError || new Error("Unable to start camera or microphone.");
  };

  const initPeerConnection = async (iceServersFromServer = []) => {
    if (pcRef.current) return;
    if (peerInitPromiseRef.current) {
      await peerInitPromiseRef.current;
      return;
    }

    isInitializingPeerRef.current = true;
    peerInitPromiseRef.current = (async () => {
    try {
      console.log("Patient initializing peer connection with ICE servers:", describeIceServers(iceServersFromServer));
      const stream = await acquireLocalMediaStream();
      
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      setCamEnabled(stream.getVideoTracks().length > 0);
      setMicEnabled(stream.getAudioTracks().length > 0);
      console.log("✅ Patient got local media stream");

      const iceServers = iceServersFromServer.length
        ? iceServersFromServer
        : [];

      if (!hasTurnServer(iceServers)) {
        console.warn("Patient missing TURN relay in ICE servers:", describeIceServers(iceServers));
        throw new Error("TURN relay missing in ICE server list");
      }

      const pc = new RTCPeerConnection({ iceServers });
      pcRef.current = pc;
      console.log("✅ Patient created peer connection");

      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });

      pc.ontrack = (event) => {
        console.log("🎬 Patient received remote stream");
        if (remoteVideoRef.current && event.streams[0]) {
          remoteVideoRef.current.srcObject = event.streams[0];
        }
      };

      pc.onicecandidate = (event) => {
        if (event.candidate && peerIdRef.current) {
          socketRef.current.emit("webrtc-ice-candidate", {
            candidate: event.candidate,
            to: peerIdRef.current,
          });
        }
      };

      pc.onconnectionstatechange = () => {
        console.log("Patient connection state:", pc.connectionState);
        if (pc.connectionState === "connected") {
          setInCall(true);
          setConnectionStatus("Connected to doctor");
        } else if (pc.connectionState === "disconnected" || pc.connectionState === "failed") {
          setInCall(false);
          setConnectionStatus("Disconnected from doctor");
          setShowEndCallModal(true);
        }
      };

      await drainIceQueue();
    } catch (err) {
      console.error("❌ Patient error initializing peer connection:", err);
      setConnectionStatus("Call unavailable");
      alert("Call cannot start without TURN relay and media access. Please try again later.");
    } finally {
      isInitializingPeerRef.current = false;
    }
    })();

    try {
      await peerInitPromiseRef.current;
    } finally {
      peerInitPromiseRef.current = null;
    }
  };

  const cleanup = () => {
    console.log("🧹 Patient cleaning up...");

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Save consultation before leaving
    if (consultationId && messages.length > 0) {
      saveConsultationSummary();
    }
    
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
    
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    
    setInCall(false);
    setConnectionStatus("Disconnected");
    peerIdRef.current = null;
    iceQueue.current = [];
    isInitializingPeerRef.current = false;
    peerInitPromiseRef.current = null;
  };

  const handleRejoin = () => {
    

    setShowEndCallModal(false);
    navigate(`/pre-join`, { state: appointment });
  };

  const handleReturnHome = () => {
    setShowEndCallModal(false);
    navigate('/home');
  };

  // ========== UI RENDER ==========

  return (
    <div className="position-relative d-flex flex-column align-items-center justify-content-center vh-100" style={{ backgroundColor: "#000", overflow: "hidden" }}>

      {/* End / Disconnect modal */}
      {showEndCallModal && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.72)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          padding: 20,
        }}>
          <div style={{
            background: "#1a1a2e",
            borderRadius: 20,
            padding: "32px 28px",
            maxWidth: 340,
            width: "100%",
            textAlign: "center",
            boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          }}>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "rgba(239,68,68,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}>
              <PhoneCall size={30} color="#ef4444" />
            </div>
            <h5 style={{ color: "#fff", fontWeight: 700, marginBottom: 6 }}>Call Ended</h5>
            <p style={{ color: "#a0aec0", fontSize: 14, marginBottom: 24 }}>
              The call has been disconnected. Would you like to rejoin or return home?
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <button
                onClick={handleRejoin}
                style={{
                  background: "#7c3aed",
                  color: "#fff",
                  border: "none",
                  borderRadius: 12,
                  padding: "13px 0",
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: "pointer",
                  boxShadow: "0 4px 14px rgba(124,58,237,0.4)",
                }}
              >
                Rejoin Call
              </button>
              <button
                onClick={handleReturnHome}
                style={{
                  background: "transparent",
                  color: "#a0aec0",
                  border: "1px solid #374151",
                  borderRadius: 12,
                  padding: "13px 0",
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: "pointer",
                }}
              >
                Return Home
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Connection status indicator */}
      <div style={{ 
        position: "absolute", 
        top: "10px", 
        left: "10px", 
        backgroundColor: inCall ? "#28a745" : "#dc3545", 
        color: "white", 
        padding: "5px 10px", 
        borderRadius: "5px",
        fontSize: "12px",
        zIndex: 1000
      }}>
        {inCall ? "Connected" : connectionStatus}
      </div>

      {/* Doctor info */}
      <div className="text-center" style={{ position: "absolute", top: "20px", width: "100%" }}>
        <h5 style={{ fontWeight: "600", color: "#fff", textShadow: "1px 1px 3px rgba(0,0,0,0.7)" }}>
          Dr. {appointment?.doctor_?.name || "Smith"}
        </h5>
      </div>

      {/* Remote video */}
      <video 
        ref={remoteVideoRef} 
        autoPlay 
        playsInline 
        style={{ 
          width: "100%", 
          height: "100%", 
          objectFit: "cover", 
          backgroundColor: "#111"
        }} 
      />

      {/* Local video */}
      <video 
        ref={localVideoRef} 
        autoPlay 
        muted 
        playsInline 
        style={{ 
          position: "absolute", 
          bottom: "90px", 
          right: "20px", 
          width: "110px", 
          height: "150px", 
          borderRadius: "10px", 
          objectFit: "cover", 
          border: "2px solid white", 
          boxShadow: "0px 4px 8px rgba(0,0,0,0.3)",
          transform: "scaleX(-1)"
        }} 
      />

      {/* Call timer */}
      {inCall && (
        <div style={{ 
          position: "absolute", 
          bottom: "160px", 
          backgroundColor: "rgba(0,0,0,0.6)", 
          color: "white", 
          padding: "4px 12px", 
          borderRadius: "20px", 
          fontSize: "13px" 
        }}>
          {formatTime(remainingTime)}
        </div>
      )}

      {/* Prescription notification */}
      {doctorPrescription && !showPrescription && (
        <div style={{ 
          position: "absolute", 
          top: "60px", 
          right: "20px", 
          backgroundColor: "#28a745",
          color: "white",
          padding: "10px 15px",
          borderRadius: "10px",
          cursor: "pointer",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          gap: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)"
        }}
        onClick={() => setShowPrescription(true)}
        >
          <Pill size={20} />
          <span>Prescription Received!</span>
        </div>
      )}

      {/* Prescription view */}
      {showPrescription && doctorPrescription && (
        <div style={{ 
          position: "absolute", 
          top: "60px", 
          right: "20px", 
          width: "350px",
          maxHeight: "500px",
          backgroundColor: "white",
          borderRadius: "10px",
          padding: "15px",
          zIndex: 1000,
          boxShadow: "0 8px 25px rgba(0,0,0,0.3)",
          overflowY: "auto"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
            <h5 style={{ margin: 0, color: "#333" }}>💊 Your Prescription</h5>
            <div style={{ display: "flex", gap: "10px" }}>
              <button 
                onClick={downloadPrescription}
                style={{ 
                  background: "#17a2b8", 
                  border: "none", 
                  color: "white", 
                  padding: "5px 10px",
                  borderRadius: "5px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px"
                }}
              >
                <Download size={14} />
                PDF
              </button>
              <button 
                onClick={() => setShowPrescription(false)}
                style={{ 
                  background: "none", 
                  border: "none", 
                  color: "#666", 
                  fontSize: "20px",
                  cursor: "pointer" 
                }}
              >
                ×
              </button>
            </div>
          </div>
          
          {/* Prescription content */}
          <div style={{ marginBottom: "15px" }}>
            <h6 style={{ color: "#666", marginBottom: "10px" }}>Medicines:</h6>
            {doctorPrescription.medicines?.map((med, index) => (
              <div key={index} style={{ 
                marginBottom: "10px", 
                padding: "10px", 
                border: "1px solid #e0e0e0", 
                borderRadius: "8px",
                backgroundColor: "#f8f9fa"
              }}>
                <div style={{ fontWeight: "bold", color: "#333", marginBottom: "5px" }}>
                  {med.name || `Medicine ${index + 1}`}
                </div>
                {med.dosage && <div>Dosage: {med.dosage}</div>}
                {med.frequency && <div>Frequency: {med.frequency}</div>}
                {med.duration && <div>Duration: {med.duration}</div>}
              </div>
            ))}
          </div>
          
          {/* More prescription details... */}
          
        </div>
      )}

      {/* Controls */}
      <div className="d-flex justify-content-center align-items-center" style={{ position: "absolute", bottom: "20px", width: "100%", gap: "15px" }}>
        {/* Prescription button */}
        {doctorPrescription && (
          <div 
            style={{ 
              backgroundColor: "#28a745", 
              padding: "10px", 
              borderRadius: "50%", 
              cursor: "pointer",
              boxShadow: "0px 2px 6px rgba(0,0,0,0.2)",
              position: "relative"
            }} 
            onClick={() => setShowPrescription(!showPrescription)}
            title="View Prescription"
          >
            <Pill color="white" size={24} />
          </div>
        )}

        {/* Chat button */}
        <div 
          style={{ 
            backgroundColor: "#fff", 
            padding: "10px", 
            borderRadius: "50%", 
            cursor: "pointer",
            boxShadow: "0px 2px 6px rgba(0,0,0,0.2)",
            position: "relative"
          }} 
          onClick={() => setChatOpen(!chatOpen)}
          title="Chat"
        >
          <MessageSquare color="#7b3aed" size={24} />
          {unreadMessages > 0 && (
            <div style={{
              position: "absolute",
              top: "-5px",
              right: "-5px",
              backgroundColor: "red",
              color: "white",
              borderRadius: "50%",
              width: "18px",
              height: "18px",
              fontSize: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              {unreadMessages}
            </div>
          )}
        </div>

        {/* End call button */}
        <button 
          onClick={() => { cleanup(); setShowEndCallModal(true); }} 
          style={{ 
            backgroundColor: "red", 
            border: "none", 
            borderRadius: "50%", 
            padding: "15px", 
            cursor: "pointer",
            boxShadow: "0px 4px 8px rgba(0,0,0,0.3)" 
          }}
          title="End Call"
        >
          <PhoneCall color="white" size={28} />
        </button>

        {/* Save button */}
        <div 
          style={{ 
            backgroundColor: "#fff", 
            padding: "10px", 
            borderRadius: "50%", 
            cursor: saving ? "not-allowed" : "pointer",
            boxShadow: "0px 2px 6px rgba(0,0,0,0.2)",
            opacity: saving ? 0.7 : 1
          }} 
          onClick={saveConsultationSummary}
          title="Save Consultation"
          disabled={saving}
        >
          <Save color={saving ? "#999" : "#007bff"} size={24} />
        </div>
      </div>

      {/* Chat panel */}
      {chatOpen && (
        <div style={{ 
          position: "absolute", 
          bottom: "80px", 
          right: "20px", 
          width: "350px", 
          height: "400px", 
          backgroundColor: "#fefefe", 
          borderRadius: "15px", 
          display: "flex", 
          flexDirection: "column", 
          boxShadow: "0 8px 20px rgba(0,0,0,0.25)", 
          overflow: "hidden", 
          fontFamily: "Arial, sans-serif",
          zIndex: 1000
        }}>
          <div style={{ 
            backgroundColor: "#7b3aed", 
            color: "white", 
            padding: "10px 15px", 
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <span>💬 Chat with Dr. {appointment?.doctor_?.name || "Smith"}</span>
            <button 
              onClick={() => setChatOpen(false)}
              style={{ 
                background: "none", 
                border: "none", 
                color: "white", 
                fontSize: "20px",
                cursor: "pointer" 
              }}
            >
              ×
            </button>
          </div>
          
          {/* Chat messages */}
          <div style={{ 
            flex: 1, 
            padding: "15px", 
            overflowY: "auto", 
            display: "flex", 
            flexDirection: "column", 
            gap: "10px",
            backgroundColor: "#f5f5f5"
          }}>
            {loadingChat ? (
              <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                Loading chat history...
              </div>
            ) : messages.length === 0 ? (
              <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                No messages yet. Start chatting!
              </div>
            ) : (
              messages.map((msg) => (
                <ChatMessage key={msg._id} message={msg} isPatient={msg.sender === "patient"} />
              ))
            )}
            <div ref={messagesEndRef}></div>
          </div>

          {/* Message input */}
          <div style={{ display: "flex", borderTop: "1px solid #ddd" }}>
            <input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") sendMessage(); }}
              placeholder="Type your message..."
              style={{ 
                flex: 1, 
                padding: "12px 15px", 
                border: "none", 
                outline: "none", 
                fontSize: "14px",
                backgroundColor: "#fff"
              }}
            />
            <button 
              onClick={sendMessage} 
              disabled={!newMessage.trim()}
              style={{ 
                padding: "0 20px", 
                backgroundColor: newMessage.trim() ? "#7b3aed" : "#ccc", 
                color: "white", 
                border: "none", 
                cursor: newMessage.trim() ? "pointer" : "not-allowed",
                fontWeight: "bold"
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Chat message component
const ChatMessage = ({ message, isPatient }) => {
  const isSystem = message.sender === "system";
  const isPrescription = message.type === "prescription";
  
  if (isPrescription || isSystem) {
    return (
      <div style={{ 
        alignSelf: "center",
        maxWidth: "85%", 
        wordBreak: "break-word"
      }}>
        <div style={{ 
          backgroundColor: "#fff3cd", 
          color: "#856404", 
          padding: "10px", 
          borderRadius: "10px",
          border: "1px solid #ffeaa7",
          textAlign: "center",
          fontSize: "13px"
        }}>
          {message.text}
        </div>
      </div>
    );
  }
  
  return (
    <div style={{ 
      alignSelf: isPatient ? "flex-end" : "flex-start",
      maxWidth: "85%", 
      wordBreak: "break-word"
    }}>
      <div style={{ 
        backgroundColor: isPatient ? "#7b3aed" : "#e5e5ea", 
        color: isPatient ? "white" : "black", 
        padding: "8px 12px", 
        borderRadius: "15px",
        borderBottomRightRadius: isPatient ? "5px" : "15px",
        borderBottomLeftRadius: isPatient ? "15px" : "5px"
      }}>
        <div style={{ fontSize: "12px", opacity: 0.8, marginBottom: "3px" }}>
          {message.senderName}
        </div>
        <div>{message.text}</div>
        <div style={{ 
          fontSize: "10px", 
          opacity: 0.7, 
          marginTop: "5px",
          textAlign: "right"
        }}>
          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
};

// Helper function for time formatting
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60).toString().padStart(2, "0");
  const secs = (seconds % 60).toString().padStart(2, "0");
  return `${mins}:${secs}`;
};

export default PatientCallRoom;